'use client';

import { FC, useState } from 'react';
import { Dialog, Text, Flex, Input, Button } from '@/_components';
import styles from './PromptTemplatesDialog.module.css';
import type {
  PromptTemplatesDialogProps,
  PromptTemplate,
} from './PromptTemplatesDialog.types';

const MOCK_PROMPT_TEMPLATES: PromptTemplate[] = [
  {
    id: 'add-work-item-in-karbon',
    title: 'Add Work Item to Karbon from Questionnaire',
    description: `
      Add a work item in Karbon with the following details:
      - Work Title: Review {questionnaire.name}
      - Client: {questionnaire.company.name}
      - Work Template: None
      - Work Type: Accounting
      - Assigned To: {advisor.name}
    `,
    category: 'Karbon',
  },
  {
    id: 'add-contact-in-karbon',
    title: 'Add Contact to Karbon from New Client',
    description: `
      Add a contact in Karbon with the following details:
      - Client: {user.name}
      - Email: {user.email}
    `,
    category: 'Karbon',
  },
  {
    id: 'add-task-in-click-up',
    title: 'Add Task to ClickUp from Questionnaire',
    description: `
      Add a task in ClickUp with the following details:
      - Title: Review {questionnaire.name}
      - Client: {advisor.name}
    `,
    category: 'ClickUp',
  },
  {
    id: 'upload-document-to-sharepoint',
    title: 'Upload Document to SharePoint',
    description: `
      Upload the following document to SharePoint:
      - Document Name: {document.name}
      - Document Path: {document.path}
      - Client: {client.name}
    `,
    category: 'SharePoint',
  },
];

const groupTemplatesByCategory = (templates: PromptTemplate[]) => {
  return templates.reduce(
    (acc, template) => {
      if (!acc[template.category]) {
        acc[template.category] = [];
      }
      acc[template.category].push(template);
      return acc;
    },
    {} as Record<string, PromptTemplate[]>,
  );
};

export const PromptTemplatesDialog: FC<PromptTemplatesDialogProps> = ({
  isOpen,
  onClose,
  onSelectTemplate,
}) => {
  const [searchTerm, setSearchTerm] = useState('');

  const filteredTemplates = MOCK_PROMPT_TEMPLATES.filter(
    (template) =>
      template.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      template.description.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const groupedTemplates = groupTemplatesByCategory(filteredTemplates);

  const handleSelect = (template: PromptTemplate) => {
    if (onSelectTemplate) {
      onSelectTemplate(template);
    }
    // Potentially close the dialog after selection, or let the parent handle it
    // onClose();
    console.log('Selected template:', template.title);
  };

  if (!isOpen) {
    return null;
  }

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title="Prompt Templates"
      className={styles.dialog}
      isFullScreen={false}
    >
      <Dialog.Content>
        <Flex isColumn gap={8} className={styles.dialogContent}>
          <Input
            label="Search templates"
            isLabelHidden
            placeholder="Search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            iconLeft="search"
            className={styles.searchInput}
          />

          <hr className={styles.horizontalLine} />

          <Flex isColumn gap={16} className={styles.templatesListContainer}>
            {Object.entries(groupedTemplates).map(([category, templates]) => (
              <Flex
                key={category}
                isColumn
                gap={8}
                className={styles.categorySection}
              >
                <Text fontWeight="bold" className={styles.categoryTitle}>
                  {category}
                </Text>
                {templates.map((template) => (
                  <Flex
                    key={template.id}
                    isRow
                    isBetween
                    isYCentered
                    className={styles.templateItem}
                  >
                    <Flex isColumn>
                      <Text fontWeight="medium">{template.title}</Text>
                      <Text type="small" color="secondary">
                        {template.description}
                      </Text>
                    </Flex>
                    <Button
                      value="Select"
                      onClick={() => handleSelect(template)}
                      size="small"
                      intent="secondary"
                      className={styles.selectButton}
                    />
                  </Flex>
                ))}
              </Flex>
            ))}

            {filteredTemplates.length === 0 && (
              <Text className={styles.noResultsText}>No templates found.</Text>
            )}
          </Flex>
        </Flex>
      </Dialog.Content>
    </Dialog>
  );
};
